import { Notification } from './notification-service'

/**
 * Globaler Notification Service für die Verwaltung von Notifications
 * außerhalb des Notification Centers
 */
export class GlobalNotificationService {
  private static notifications: Notification[] = []
  private static unreadCount: number = 0
  private static listeners: Set<(notifications: Notification[], unreadCount: number) => void> = new Set()

  /**
   * Setzt die globalen Notifications
   */
  static setNotifications(notifications: Notification[], unreadCount: number): void {
    this.notifications = notifications
    this.unreadCount = unreadCount
    
    // Benachrichtige alle Listener
    this.listeners.forEach(listener => {
      listener(notifications, unreadCount)
    })
  }

  /**
   * Fügt eine neue Notification hinzu
   */
  static addNotification(notification: Notification): void {
    this.notifications = [notification, ...this.notifications]
    if (!notification.is_read) {
      this.unreadCount++
    }
    
    // Benachrichtige alle Listener
    this.listeners.forEach(listener => {
      listener(this.notifications, this.unreadCount)
    })
  }

  /**
   * Aktualisiert eine bestehende Notification
   */
  static updateNotification(notification: Notification): void {
    const index = this.notifications.findIndex(n => n.id === notification.id)
    if (index >= 0) {
      const oldNotification = this.notifications[index]
      this.notifications[index] = notification
      
      // Aktualisiere unreadCount basierend auf Änderungen
      if (oldNotification.is_read && !notification.is_read) {
        this.unreadCount++
      } else if (!oldNotification.is_read && notification.is_read) {
        this.unreadCount--
      }
      
      // Benachrichtige alle Listener
      this.listeners.forEach(listener => {
        listener(this.notifications, this.unreadCount)
      })
    }
  }

  /**
   * Entfernt eine Notification
   */
  static removeNotification(notificationId: string): void {
    const notification = this.notifications.find(n => n.id === notificationId)
    if (notification) {
      this.notifications = this.notifications.filter(n => n.id !== notificationId)
      if (!notification.is_read) {
        this.unreadCount--
      }
      
      // Benachrichtige alle Listener
      this.listeners.forEach(listener => {
        listener(this.notifications, this.unreadCount)
      })
    }
  }

  /**
   * Gibt die aktuellen Notifications zurück
   */
  static getNotifications(): Notification[] {
    return [...this.notifications]
  }

  /**
   * Gibt die Anzahl ungelesener Notifications zurück
   */
  static getUnreadCount(): number {
    return this.unreadCount
  }

  /**
   * Registriert einen Listener für Notification-Updates
   */
  static addListener(listener: (notifications: Notification[], unreadCount: number) => void): () => void {
    this.listeners.add(listener)
    
    // Sofort mit aktuellen Daten aufrufen (jetzt sicher durch useCallback)
    listener(this.notifications, this.unreadCount)
    
    // Gibt eine Cleanup-Funktion zurück
    return () => {
      this.listeners.delete(listener)
    }
  }

  /**
   * Entfernt alle Listener
   */
  static clearListeners(): void {
    this.listeners.clear()
  }

  /**
   * Setzt den Service zurück (beim Logout)
   */
  static reset(): void {
    this.notifications = []
    this.unreadCount = 0
    this.listeners.clear()
  }
}
